import React from 'react';
import { motion } from 'framer-motion';
import { ServiceCard } from '../components/PaymentFlow';
import { CheckIcon, XIcon } from 'lucide-react';
export const Services: React.FC = () => {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const services = [{
    id: 'basic',
    title: 'Basic Business Exit',
    price: 'POA',
    features: ['Simple company transfer', 'Limited debts (under £50k)', 'No major creditor issues', 'No ongoing leases', 'Standard documentation', 'Email support', '30-day completion target']
  }, {
    id: 'urgent',
    title: 'Urgent Rescue',
    price: 'POA',
    recommended: true,
    features: ['Expedited company transfer', 'Medium debts (£50k-£150k)', 'Some creditor arrears', 'Simple lease commitments', 'Priority documentation', 'WhatsApp & email support', '14-day completion target']
  }, {
    id: 'complex',
    title: 'Complex Rescue',
    price: 'POA',
    features: ['Comprehensive company transfer', 'Significant debts (£150k+)', 'Substantial creditor issues', 'Multiple lease commitments', 'Complex documentation', 'Priority support via all channels', 'Tailored completion timeline']
  }];
  const comparisonData = [{
    feature: 'Company Transfer',
    basic: true,
    urgent: true,
    complex: true
  }, {
    feature: 'Debt Level Handled',
    basic: 'Up to £50k',
    urgent: 'Up to £150k',
    complex: 'Over £150k'
  }, {
    feature: 'Creditor Issues',
    basic: false,
    urgent: 'Basic arrears',
    complex: 'Complex issues'
  }, {
    feature: 'Lease Commitments',
    basic: false,
    urgent: 'Simple leases',
    complex: 'Multiple leases'
  }, {
    feature: 'Creditor Pressure',
    basic: 'Low',
    urgent: 'Medium',
    complex: 'High'
  }, {
    feature: 'Completion Time',
    basic: '30 days',
    urgent: '14 days',
    complex: 'Tailored'
  }, {
    feature: 'Support Channels',
    basic: 'Email only',
    urgent: 'Email & WhatsApp',
    complex: 'All channels'
  }];
  return <div className="bg-white" data-id="element-442">
      {/* Hero Section */}
      <section className="bg-black text-white py-16 md:py-24" data-id="element-443">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-444">
          <motion.div className="max-w-3xl mx-auto text-center" initial="hidden" animate="visible" variants={fadeIn} data-id="element-445">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" data-id="element-446">
              Services & Pricing
            </h1>
            <p className="text-xl text-gray-300" data-id="element-447">
              Transparent pricing based on the complexity of your business
              situation. Select the package that best matches your needs.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Service Explanation */}
      <section className="py-16 md:py-20 bg-white" data-id="element-448">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-449">
          <motion.div className="max-w-3xl mx-auto" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-450">
            <h2 className="text-3xl font-bold mb-6" data-id="element-451">How Our Service Works</h2>
            <div className="prose prose-lg max-w-none" data-id="element-452">
              <p data-id="element-453">
                If you own a Ltd company that's drowning in debt, bills, and
                overwhelming overheads, we can help you find a way forward.
              </p>
              <p data-id="element-454">
                <strong data-id="element-455">Debt Rescue</strong> specialises in sourcing third-party
                buyers who are willing to take your company off your hands.
                These buyers will:
              </p>
              <ul data-id="element-456">
                <li data-id="element-457">Take on all existing company liabilities</li>
                <li data-id="element-458">Assume full responsibility for the business</li>
                <li data-id="element-459">Handle the challenges that come with the company</li>
              </ul>
              <p data-id="element-460">
                In return for this service, you pay a sourcing fee that varies
                based on the complexity of your situation. This approach allows
                you to walk away from your business problems and start fresh
                without the burden of company debt.
              </p>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 my-8" data-id="element-461">
                <h3 className="text-xl font-bold mb-2" data-id="element-462">Important Note</h3>
                <p className="text-gray-700" data-id="element-463">
                  This service is specifically for limited company owners. We do
                  not provide advice on personal debt, sole trader businesses,
                  or partnerships. Our service is not insolvency practitioner
                  work and we do not provide financial advice.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Pricing Cards */}
      <section className="py-12 md:py-16 bg-gray-50" data-id="element-464">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-465">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-466">
            <h2 className="text-3xl font-bold mb-4" data-id="element-467">Our Pricing Options</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-468">
              Choose the package that best fits your company's situation.
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-469">
            {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-470" />)}
          </motion.div>
        </div>
      </section>
      {/* Comparison Table */}
      <section className="py-16 md:py-24 bg-white" data-id="element-471">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-472">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-473">
            <h2 className="text-3xl font-bold mb-4" data-id="element-474">Service Comparison</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-475">
              Compare our packages to find the right solution for your business.
            </p>
          </motion.div>
          <motion.div className="overflow-x-auto" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-476">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-477">
              <thead className="bg-gray-50" data-id="element-478">
                <tr data-id="element-479">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-480">
                    Feature
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-481">
                    Basic Business Exit
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-red-50" data-id="element-482">
                    Urgent Rescue
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-483">
                    Complex Rescue
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-484">
                {comparisonData.map((item, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-485">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-486">
                      {item.feature}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-487">
                      {typeof item.basic === 'boolean' ? item.basic ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-488" /> : <XIcon className="h-5 w-5 text-red-500" data-id="element-489" /> : item.basic}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 bg-red-50" data-id="element-490">
                      {typeof item.urgent === 'boolean' ? item.urgent ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-491" /> : <XIcon className="h-5 w-5 text-red-500" data-id="element-492" /> : item.urgent}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-493">
                      {typeof item.complex === 'boolean' ? item.complex ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-494" /> : <XIcon className="h-5 w-5 text-red-500" data-id="element-495" /> : item.complex}
                    </td>
                  </tr>)}
                <tr data-id="element-496">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-497">
                    Price
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-498">
                    POA
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 bg-red-50" data-id="element-499">
                    POA
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-500">
                    POA
                  </td>
                </tr>
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>
      {/* Payment Information */}
      <section className="py-16 md:py-20 bg-gray-50" data-id="element-501">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-502">
          <motion.div className="max-w-3xl mx-auto" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-503">
            <h2 className="text-3xl font-bold mb-6" data-id="element-504">Get Your Quote</h2>
            <div className="bg-white p-8 rounded-lg shadow-sm" data-id="element-505">
              <h3 className="text-xl font-bold mb-4" data-id="element-506">Pricing on Application</h3>
              <div className="space-y-6" data-id="element-507">
                <p className="text-gray-700" data-id="element-508">
                  We provide personalised quotes based on your specific
                  situation. Contact us via your preferred method to discuss
                  your requirements and receive a tailored quote.
                </p>
                <div className="bg-gray-50 p-4 rounded border border-gray-200" data-id="element-509">
                  <h4 className="font-bold text-lg mb-2" data-id="element-510">
                    What We'll Need to Know:
                  </h4>
                  <ul className="space-y-2 text-gray-700" data-id="element-511">
                    <li data-id="element-512">• Total amount of company debts and liabilities</li>
                    <li data-id="element-513">• Types of creditors involved</li>
                    <li data-id="element-514">• Any lease commitments or ongoing contracts</li>
                    <li data-id="element-515">• Timeline requirements</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};